import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  getCustomerId,
  updateCustomer,
  getCountries,
  getStates,
  getCities
} from '../../../store/actions';
import { getCustomer } from '../../../utils/helpers';

import Loader from '../../../assets/components/loader';

import AccountSkeleton from './paymentSkeleton';

import { Country, State, City } from 'country-state-city';
import { CountryCodes } from '../../../shared/CountryCodes';
import { Industries } from '../../../shared/Industries';

const industries = Industries;
const dialCodes = CountryCodes;

const MyAccount = () => {

  const customerId = getCustomer();
  const dispatch = useDispatch();
  const myAccountInfo = useSelector((state) => state.myAccount);
  const navigate = useNavigate();

  useEffect(() => {
    if (customerId === "") {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    if (!myAccountInfo.userRequested){
      dispatch(getCustomerId())
    }
  }, [myAccountInfo.userRequested])

  const [user, setUser] = useState({
    firstName: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    lastName: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    company: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfIndustry: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfWebsite: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    email: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfCountry: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfState: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfCity: {
      value: "",
      focused: false,
      required: false,
      error: ""
    },
    cfAddress: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfDialCountryCode: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfSinglePhoneNumber: {
      value: "",
      focused: false,
      required: true,
      error: ""
    },
    cfZip: {
      value: "",
      focused: false,
      required: false,
      error: ""
    }
  })

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    setStates(myAccountInfo.states)
  }, [myAccountInfo.states])

  useEffect(() => {
    setCities(myAccountInfo.cities)
  }, [myAccountInfo.cities])

  useEffect(() => {
    if (!myAccountInfo.countriesRequested) {
      dispatch(getCountries())
    }
  }, [myAccountInfo.countriesRequested])

  useEffect(() => {
    if (user && user.cfCountry && user.cfCountry.value !== "") {
      dispatch(getStates({ isoCode: user.cfCountry.value, countryId : ""}))
    }
  }, [user.cfCountry.value])

  useEffect(() => {
    if (user.cfCountry.value !== "" && user.cfState.value !== "") {
      dispatch(getCities({countryId: "", stateId: "", countryCode: user.cfCountry.value, isoCode: user.cfState.value }))
    }
  }, [user.cfState.value])

  useEffect(() => {
    if (Object.keys(myAccountInfo.user).length > 0) {
      let currentKeys = Object.keys(user)
      currentKeys.map((key) => {
        if (myAccountInfo.user.hasOwnProperty([key])) {
          setUser(prevUser => ({
            ...prevUser,
            [key]: {
              ...prevUser[key],
              value: myAccountInfo.user[key],
              focused: true,
              error: prevUser[key].required ? validate(key, myAccountInfo.user[key]) : ""
            }
          }))
        }
      })
    }
  }, [myAccountInfo.user])

  const handleInputChange = (ev) => {
    if (ev.target.name === "cfCountry"){
      setUser(prevUser => ({
        ...prevUser,
        [ev.target.name]: {
          ...prevUser["cfCity"],
          value: "",
          focused: true,
          error: prevUser["cfCity"].required ? validate("cfCity", "") : ""
        }
      }))
    }
    setUser(prevUser => ({
      ...prevUser,
      [ev.target.name]: {
        ...prevUser[ev.target.name],
        value: ev.target.value,
        focused: true,
        error: prevUser[ev.target.name].required ? validate(ev.target.name, ev.target.value) : ""
      }
    }))
  }

  const handleOnfocus = (ev) => {
    if (ev.target.name === "cfDialCountryCode" || ev.target.name === "cfSinglePhoneNumber") {
      setUser(prevUser => ({
        ...prevUser,
        cfDialCountryCode: {
          ...prevUser.cfDialCountryCode,
          focused: true,
          error: prevUser.cfDialCountryCode.required ? validate("cfDialCountryCode", prevUser.cfDialCountryCode.value) : ""
        },
        cfSinglePhoneNumber: {
          ...prevUser.cfSinglePhoneNumber,
          focused: true,
          error: prevUser.cfSinglePhoneNumber.required ? validate("cfSinglePhoneNumber", prevUser.cfSinglePhoneNumber.value) : ""
        }
      }))
    }
    setUser(prevUser => ({
      ...prevUser,
      [ev.target.name]: {
        ...prevUser[ev.target.name],
        focused: true,
        error: prevUser[ev.target.name].required ? validate(ev.target.name, ev.target.value) : ""
      }
    }))
  }

  const validate = (name, value) => {
    switch (name) {
      case ("firstName"):
        if (value.length === 0) {
          return "First name is required"
        } else if (!/^[A-Za-z\s]+$/.test(value)) {
          return "First name is not valid"
        } else {
          return ""
        }
      case ("lastName"):
        if (value.length === 0) {
          return "Last name is required"
        } else if (!/^[A-Za-z\s]+$/.test(value)) {
          return "Last name is not valid"
        } else {
          return ""
        }
      case ("company"):
        if (value.length === 0) {
          return "Company is required"
        } else {
          return ""
        }
      case ("cfIndustry"):
        if (value.length === 0) {
          return "Industry is required"
        } else {
          return ""
        }
      case ("cfWebsite"):
        if (value.length === 0) {
          return "Website is required"
        } else if (!/^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z]{2,}){1,})(\/\S*)?$/.test(value)) {
          return "Website is not valid"
        } else {
          return ""
        }
      case ("email"):
        if (value.length === 0) {
          return "Email is required"
        } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) === false) {
          return "Email is not valid"
        } else {
          return ""
        }
      case ("cfCountry"):
        if (value.length === 0) {
          return "Country is required"
        } else {
          return ""
        }
      case ("cfState"):
        if (value.length === 0) {
          return "State is required"
        } else {
          return ""
        }
      case ("cfCity"):
        if (value.length === 0) {
          return "City is required"
        } else {
          return ""
        }
      case ("cfAddress"):
        if (value.length === 0) {
          return "Address is required"
        } else {
          return ""
        }
      case ("cfDialCountryCode"):
        if (value === "") {
          return "Dial code is required"
        } else {
          return ""
        }
      case ("cfSinglePhoneNumber"):
        if (value.length === 0) {
          return "Phone number is required"
        } else if (!/^\d+$/.test(value)) {
          return "Phone number is not valid"
        } else if (value.length <= 7) {
          return "Phone number must be longer"
        } else if (value.length >= 12) {
          return "Phone number must be shorter"
        } else {
          return ""
        }
      default:
        return ""
    }
  }

  const inputStatus = (key) => {
    if (user[key].focused) {
      if (user[key].error === "") {
        return "input--group input--success"
      } else {
        return "input--group input--error"
      }
    } else {
      return "input--group"
    }
  }

  const updateCustomerData = (ev) => {
    ev.preventDefault();
    let customerData = {
      first_name: user.firstName.value,
      last_name: user.lastName.value,
      email: user.email.value,
      company: user.company.value,
      cf_dial_country_code: user.cfDialCountryCode.value,
      cf_single_phone_number: user.cfSinglePhoneNumber.value,
      cf_industry: user.cfIndustry.value,
      cf_website: user.cfWebsite.value,
      cf_country: user.cfCountry.value,
      cf_state: user.cfState.value,
      cf_city: user.cfCity.value,
      cf_address: user.cfAddress.value,
      cf_zip: user.cfZip.value
    }
    if (customerData.cf_zip === ""){
      delete customerData["cf_zip"]
    }
    dispatch(updateCustomer(customerData));
  }

  function disabledSubmit() {
    if (myAccountInfo.loading){
      return true
    }
    let keys = Object.keys(user)
    for (let i = 0; i < keys.length; i++) {
      if (user[keys[i]].required) {
        if (user[keys[i]].focused && user[keys[i]].error !== "") {
          return true
        } else if (!user[keys[i]].focused) {
          return true
        }
      }
    }
    return false
  }

  if (!myAccountInfo.countriesObtained && !myAccountInfo.userObtained) {
    return (
      <AccountSkeleton/>
    )
  }

  return (
    <>
      <div className='my--account'>
        <form className='container marquet--form two--columns--form' onSubmit={(ev) => { updateCustomerData(ev) }}>
          <div className="marquet--form--group">
            <div className={inputStatus("firstName")}>
              <label>
                FIRST NAME<b> *</b>
              </label>
              <input
                name="firstName"
                type="text"
                maxLength={30}
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.firstName.value} />
            </div>
            <div className="invalid--feedback">{user.firstName.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("lastName")}>
              <label>
                LAST NAME<b> *</b>
              </label>
              <input
                name="lastName"
                type="text"
                maxLength={30}
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.lastName.value} />
            </div>
            <div className="invalid--feedback">{user.lastName.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("company")}>
              <label>
                COMPANY<b> *</b>
              </label>
              <input
                name="company"
                type="text"
                maxLength={50}
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.company.value} />
            </div>
            <div className="invalid--feedback">{user.company.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("cfIndustry")}>
              <label>
                INDUSTRY<b> *</b>
              </label>
              <select
                name="cfIndustry"
                type="select"
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.cfIndustry.value}>
                <option value="">Select one</option>
                {industries && industries.length > 0 && industries.map((industry, index) => (
                  <option key={index} value={industry.name}>
                    {industry.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="invalid--feedback">{user.cfIndustry.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("cfWebsite")}>
              <label>
                WEBSITE URL<b> *</b>
              </label>
              <input
                name="cfWebsite"
                type="website"
                maxLength={50}
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.cfWebsite.value} />
            </div>
            <div className="invalid--feedback">{user.cfWebsite.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("email")}>
              <label>
                EMAIL<b> *</b>
              </label>
              <input
                name="email"
                type="email"
                maxLength={50}
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.email.value} />
            </div>
            <div className="invalid--feedback">{user.email.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("cfCountry")}>
              <label>
                COUNTRY<b> *</b>
              </label>
              <select
                name="cfCountry"
                type="select"
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.cfCountry.value}>
                <option value="">Select one</option>
                {myAccountInfo && myAccountInfo.countries && myAccountInfo.countries.length > 0 && myAccountInfo.countries.map((country, index) => {
                  return (
                    <option key={index} value={country.isoCode}>
                      {country.name}
                    </option>
                  )
                })}
              </select>
            </div>
            <div className="invalid--feedback">{user.cfCountry.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("cfState")}>
              <label>
                STATE / PROVINCE<b> *</b>
              </label>
              {myAccountInfo.statesRequested ?
                <div className="input--loading">
                  <Loader size="xs" />
                </div>
                :
                <select
                  name="cfState"
                  type="select"
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={user.cfState.value}
                  style={myAccountInfo.statesRequested ? { cursor: "progress !important" } : { cursor: "default" }}
                  disabled={myAccountInfo.statesRequested}>
                  <option value="">Select one</option>
                  {states && states.length > 0 && states.map((state, index) => (
                    <option key={index} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))
                  }
                </select>
              }
            </div>
            <div className="invalid--feedback">{user.cfState.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className='input--group'>
              <label>
                CITY{/*<b> *</b>*/}
              </label>
              {myAccountInfo.statesRequested || myAccountInfo.citiesRequested ?
                <div className="input--loading">
                  <Loader size="xs" />
                </div>
                :
                <select
                  name="cfCity"
                  type="select"
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={user.cfCity.value}
                  style={myAccountInfo.citiesRequested || myAccountInfo.statesRequested ? { cursor: "progress !important" } : { cursor: "default" }}
                  disabled={myAccountInfo.citiesRequested || myAccountInfo.statesRequested}>
                  <option value="">Select one</option>
                  {cities && cities.length > 0 && cities.map((city, index) => {
                    return (
                      <option key={index} value={city.name}>
                        {city.name}
                      </option>
                    )
                  })
                  }
                </select>
              }
            </div>
            <div className="invalid--feedback">{user.cfCity.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={inputStatus("cfAddress")}>
              <label>
                ADDRESS<b> *</b>
              </label>
              <input
                name="cfAddress"
                type="text"
                maxLength={50}
                onChange={ev => handleInputChange(ev)}
                onFocus={ev => handleOnfocus(ev)}
                value={user.cfAddress.value} />
            </div>
            <div className="invalid--feedback">{user.cfAddress.error}</div>
          </div>
          <div className="marquet--form--group">
            <div className={
              user.cfDialCountryCode.focused || user.cfSinglePhoneNumber.focused ?
                user.cfDialCountryCode.error === "" && user.cfSinglePhoneNumber.error === "" ?
                  "input--group input--success" : "input--group input--error"
                : "input--group"
            }>
              <label>
                PHONE<b> *</b>
              </label>
              <div className="row--form">
                <select
                  name="cfDialCountryCode"
                  type="select"
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={user.cfDialCountryCode.value}>
                  <option value="">Select one</option>
                  {dialCodes && dialCodes.length > 0 && dialCodes.map((country, index) => {
                    return (
                      <option key={index} value={country.dialCode}>
                        {`${country.name} (${country.dialCode})`}
                      </option>
                    )
                  })}
                </select>
                <input
                  style={{borderBottom : "1px solid #BDBDBD"}}
                  name="cfSinglePhoneNumber"
                  type="text"
                  maxLength={15}
                  onChange={ev => handleInputChange(ev)}
                  onFocus={ev => handleOnfocus(ev)}
                  value={user.cfSinglePhoneNumber.value} />
              </div>
            </div>
            <div className="invalid--feedback">{`${user.cfDialCountryCode.error} ${user.cfSinglePhoneNumber.error}`}</div>
          </div>
          <div className="marquet--form--group">
            <div className='input--group'>
              <label>ZIP / POSTAL CODE</label>
              <input
                name="cfZip"
                type="text"
                maxLength={15}
                onChange={ev => handleInputChange(ev)}
                value={user.cfZip.value} />
            </div>
          </div>
          <div className="marquet--button--group button-end">
            <button
              className="custom--button custom--button--marquet"
              disabled={disabledSubmit()}
              type='submit'>
              Update
              {myAccountInfo.loading && <Loader size="xs" />}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default MyAccount;